import { select, text } from '@clack/prompts'
import as from 'ansis'
import { Command, Option } from 'commander'
import { z } from 'zod'

import { BaseCollections } from '@scalar-org/entities/database'
import { SdkLanguage } from '@scalar-org/entities/sdk'
import { teamSchema } from '@scalar-org/entities/team'
import { namespaceSchema, slugSchema } from '@scalar-org/entities/utility'

import { config } from '@/config'
import { apiService, getAuthData, getDatabaseClient } from '@/helpers'
import { output } from '@/helpers/std-out'

export const CreateSdkCommand = () => {
  const cmd = new Command('create')

  cmd.description('Create a new SDK.')

  cmd.option('-a, --api <api>', 'Registry API slug')
  cmd.option('-n, --namespace <namespace>', 'Team namespace')
  cmd.addOption(
    new Option('-l, --language <language>', 'Language of your SDK').choices(
      Object.values(SdkLanguage),
    ),
  )

  cmd.action(async (opts) => {
    const { error, data } = z
      .object({
        namespace: namespaceSchema.optional(),
        language: z.enum(SdkLanguage).optional(),
        api: slugSchema.optional(),
      })
      .safeParse(opts)

    if (error)
      return output
        .error()
        .title('Invalid input')
        .message(error.issues[0].message)
        .exit('error')

    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    const db = await getDatabaseClient()

    const client = await apiService()

    const language =
      opts.language ||
      (await text({
        message: 'Please choose a language for your SDK',
        placeholder: 'typescript',
        validate: (v) => {
          return z.enum(SdkLanguage).safeParse(v)?.error
            ? 'Please select a valid language'
            : undefined
        },
        defaultValue: undefined,
      }))

    const team = await db.getOne({
      collectionPath: BaseCollections.Teams,
      query: {
        uid: auth.teamUid,
      },
      schema: teamSchema,
    })

    if (team.error)
      return output
        .error()
        .title('An error occurred when fetching your team.')
        .message(team.message)
        .exit('error')

    /**
     * Most teams will have 1 namespace, so we default to that.
     * Ideally we should also have a global cli namespace selected.
     */
    const namespace = data.namespace || team.data.namespaces.at(0)

    if (!namespace) {
      return output
        .error()
        .title('Missing namespace')
        .message(
          'Could not find a valid namespace for your API. Please specify a namespace or contact support.',
        )
        .exit('error')
    }

    const namespaceApisRes = await client.managedDocs.getApiDocs(namespace)

    if (namespaceApisRes.error)
      return output.error().title(namespaceApisRes.message).exit('error')

    const apiUid =
      namespaceApisRes.data.find((api) => api.slug === data.api)?.uid ||
      ((await select({
        message: 'Please select an API',
        options: namespaceApisRes.data.map((api) => ({
          value: api.uid,
          label: api.slug,
        })),
      })) as string)

    if (!apiUid) {
      return output
        .error()
        .title('No API found')
        .message('Please provide a valid API slug')
        .exit('error')
    }

    const logger = output.info().loader('Creating the SDK')
    const response = await client.sdks.create({
      apiUid,
      language,
    })

    if (response.error) {
      return output
        .error()
        .title('Something went wrong')
        .message(response.message)
        .exit('error')
    }

    logger.update('loader', 0, { content: 'SDK created successfully!' })

    const dashboardUrl = `${config.projects.dashboard}/sdk/${response.data}`

    logger
      .message(
        `View your SDK in the Scalar dashboard: ${as.cyan(dashboardUrl)}`,
      )
      .exit('success')
  })

  return cmd
}
